import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { notify } from "../../../../utils/notify";

import { findCustomerRanking } from "../../../../repositories/qerpapi/erp/relatorio/RankingClienteRepository";
import { findReceiptSituations } from "../../../../repositories/qerpapi/v3/SituacoesRepository";
import { periods } from "../../constants";
import {
  customerRankingColumns,
  customerRankingInitialFilters,
  origins,
  situations,
} from "../RankingClienteConstants";

const RankingClienteContext = createContext();

const RankingClienteProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...customerRankingInitialFilters });

  const [filterPeriod, setFilterPeriod] = useState({
    ...customerRankingInitialFilters.searchDate,
  });

  const [isMainFetchEnabled, setMainFetchEnable] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  const [openedDrawer, setDrawerOpen] = useState(null);

  const [columns, setColumns] = useState(customerRankingColumns);

  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedSituations, setCachedSituations] = useState([]);
  const [checkedOrigins, setCheckedOrigins] = useState(origins);

  const [cachedMovementData, setCachedMovementData] = useState([]);
  const [cachedPeriodTotal, setCachedPeriodTotal] = useState();
  const [cachedPeriod, setCachedPeriod] = useState({});

  const [quickSituations, setQuickSituations] = useState(situations);

  const [isIndeterminate, setIndeterminate] = useState(false);
  const [isOriginIndeterminate, setOriginIndeterminate] = useState(false);

  // ----------------------------------------------------------------------------

  const setSelectedCompany = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      multicompany: newValue,
    }));
  }, []);

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === periods.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setDateFilter = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, dateFilter: newValue }));
  }, []);

  const setRankCriteria = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, rankCriteria: newValue }));
  }, []);

  const setSituations = useCallback(() => {
    const checkedSituations = cachedSituations
      .filter((situation) => situation.checked)
      .map((situation) => situation.id);

    const checkedQuickSituations = quickSituations
      .filter((quickSituation) => quickSituation.checked)
      .map((quickSituation) => quickSituation.id);

    setFilters((prev) => ({
      ...prev,
      status: checkedSituations,
      quickStatus: checkedQuickSituations,
    }));
  }, [cachedSituations, quickSituations]);

  const setOrigins = useCallback(() => {
    const origin = checkedOrigins
      .filter((origin) => origin.checked)
      .map((origin) => origin.id);

    setFilters((prev) => ({
      ...prev,
      origin,
    }));
  }, [checkedOrigins]);

  // ----------------------------------------------------------------------------

  const applyPeriod = useCallback(
    (periodDates) => {
      setSearchDate(periodDates);
      setCachedPeriod(periodDates);
      setCustomDatePeriod({
        startDate: periodDates?.startDate
          ? moment(periodDates.startDate, "YYYY-MM-DD").toDate()
          : moment().startOf("month").toDate(),
        endDate: periodDates?.endDate
          ? moment(periodDates.endDate, "YYYY-MM-DD").toDate()
          : moment().endOf("month").toDate(),
      });
    },
    [setCustomDatePeriod, setSearchDate]
  );

  useEffect(() => {
    setSearchDate(filterPeriod);
  }, [filterPeriod, setSearchDate]);

  // ----------------------------------------------------------------------------

  const {
    isLoading: mainLoading,
    refetch,
    isRefetching: mainRefetchLoading,
  } = useQuery(
    "ranking-cliente",
    async () => await findCustomerRanking(filters),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken") && isMainFetchEnabled,
      onSuccess: async (res) => await handleMainData(res),
    }
  );

  const handleMainData = useCallback(
    async (res) => {
      const data = res?.data;

      if (!res?.success) {
        if (res.statusCode === 401) {
          notify("Token expirado", true, "error");
        }
        notify(res.message, true, "error");
      }
      applyPeriod(data?.searchPeriod);
      setCachedMovementData(data?.movements || []);
      setCachedPeriodTotal(data?.total);
      setIsGrouped(true);
    },
    [applyPeriod]
  );

  // ----------------------------------------------------------------------------

  const { isLoading: loadingCompanies } = useQuery(
    "companies",
    async () => await findCompaniesV3(),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
      onSuccess: (companies) => handleCompaniesData(companies),
    }
  );

  const validateIfCompanyIdExistAndSetNewDefault = useCallback(
    async (userCompanies) => {
      const companiesIds = Object.values(userCompanies);

      if (!companiesIds.includes(filters.multicompany)) {
        setSelectedCompany(userCompanies.Todos);
      }
    },
    [filters.multicompany, setSelectedCompany]
  );

  const handleCompaniesData = useCallback(
    async (companies) => {
      const data = companies?.data ?? [];

      if (!companies.success) notify(companies.message, true, "error");

      if (companies?.data) {
        const allCompaniesString = data.map((company) => company.id).join(",");
        const formatedCompanies = {
          Todos: allCompaniesString,
        };

        data.forEach(
          (company) => (formatedCompanies[company.nomeempresa] = company.id)
        );

        await validateIfCompanyIdExistAndSetNewDefault(formatedCompanies);

        setCachedCompanies(formatedCompanies);
        setMainFetchEnable(true);
      }
    },
    [validateIfCompanyIdExistAndSetNewDefault]
  );

  // ----------------------------------------------------------------------------

  const { isLoading: isSituationsLoading } = useQuery(
    "situations",
    async () =>
      await findReceiptSituations(sessionStorage.getItem("empresa_cnpj")),
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!sessionStorage.getItem("empresa_cnpj"),
      onSuccess: (situations) => handleSituationsData(situations),
    }
  );

  const handleSituationsData = useCallback(async (situations) => {
    const data = situations?.data ?? [];

    if (!situations.success) notify(situations.message, true, "error");

    if (data.length) {
      const formattedSituations = data
        .sort((situationA, situationB) =>
          situationA.descricao < situationB.descricao ? -1 : 1
        )
        .map((dataItem) => {
          return {
            id: dataItem.codigo,
            desc: dataItem.descricao,
            checked: false,
          };
        });

      setCachedSituations(formattedSituations);
    }
  }, []);

  // ----------------------------------------------------------------------------

  const handleCheckAll = useCallback(
    (check) => {
      const checekdSituations = cachedSituations.map((situation) => {
        situation.checked = check;
        return situation;
      });
      setIndeterminate(
        checekdSituations.some((situation) => situation.checked)
      );
      setCachedSituations(checekdSituations);
    },
    [cachedSituations]
  );

  const handleCheckSituations = useCallback(
    (checkedSituation, check) => {
      const checekdSituations = cachedSituations.map((situation) => {
        if (checkedSituation.id === situation.id) {
          situation.checked = check;
        }
        return situation;
      });

      const isAllSituationsCheckeds = checekdSituations.every(
        (checkedSituation) => checkedSituation.checked
      );
      const isSomeSituationsCheckeds = checekdSituations.some(
        (checkedSituation) => checkedSituation.checked
      );

      setCachedSituations(checekdSituations);

      if (isAllSituationsCheckeds) {
        handleCheckAll(check);
        return;
      }

      setIndeterminate(isSomeSituationsCheckeds);
    },
    [cachedSituations, handleCheckAll]
  );

  const handleCheckQuickSituations = useCallback(
    (checkedSituation, check) => {
      const checkedSituations = quickSituations.map((situation) => {
        if (checkedSituation.id === situation.id) {
          situation.checked = check;
        }
        return situation;
      });

      setQuickSituations(checkedSituations);
    },
    [quickSituations]
  );

  const handleRemoveChip = useCallback(
    (checkedSituation) => {
      const isQuickSituation = quickSituations.some(
        (quick) => quick.id === checkedSituation.id
      );
      const isSituation = cachedSituations.some(
        (situation) => situation.id === checkedSituation.id
      );

      if (isQuickSituation) {
        handleCheckQuickSituations(checkedSituation, false);
      }

      if (isSituation) {
        handleCheckSituations(checkedSituation, false);
      }

      setSituations();
    },
    [
      cachedSituations,
      handleCheckQuickSituations,
      handleCheckSituations,
      quickSituations,
      setSituations,
    ]
  );

  const clearAllSituationsFilters = useCallback(() => {
    handleCheckAll(false);
    const checkedSituations = quickSituations.map((situation) => {
      situation.checked = false;

      return situation;
    });

    setQuickSituations(checkedSituations);
    setQuickSituations(checkedSituations);
  }, [handleCheckAll, quickSituations]);

  // ----------------------------------------------------------------------------

  const handleCheckAllOrigins = useCallback(
    (check) => {
      const checkOrigins = checkedOrigins.map((origin) => {
        origin.checked = check;
        return origin;
      });
      setOriginIndeterminate(
        checkOrigins.some((situation) => situation.checked)
      );
      setCheckedOrigins(checkOrigins);
    },
    [checkedOrigins]
  );

  const handleCheckOrigins = useCallback(
    (checkedSituation, check) => {
      const checkOrigins = checkedOrigins.map((origin) => {
        if (checkedSituation.id === origin.id) {
          origin.checked = check;
        }
        return origin;
      });

      const isAllOriginsCheckeds = checkOrigins.every(
        (checkedSituation) => checkedSituation.checked
      );
      const isSomeOriginChecked = checkOrigins.some(
        (checkedSituation) => checkedSituation.checked
      );

      setCheckedOrigins(checkOrigins);

      if (isAllOriginsCheckeds) {
        handleCheckAllOrigins(check);
        return;
      }

      setOriginIndeterminate(isSomeOriginChecked);
    },
    [checkedOrigins, handleCheckAllOrigins]
  );

  const handleRemoveOriginChip = useCallback(
    (checkedOrigin) => {
      handleCheckOrigins(checkedOrigin, false);

      setOrigins();
    },
    [handleCheckOrigins, setOrigins]
  );

  const clearAllOriginsFilters = useCallback(() => {
    handleCheckAllOrigins(false);
  }, [handleCheckAllOrigins]);

  // ----------------------------------------------------------------------------

  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      filterPeriod,
      setFilterPeriod,
      setCachedSituations,

      columns,
      setColumns,

      isGrouped,

      openedDrawer,
      setDrawerOpen,

      mainLoading,
      refetch,
      mainRefetchLoading,

      cachedMovementData,
      cachedPeriod,
      cachedPeriodTotal,

      cachedSituations,
      isSituationsLoading,

      quickSituations,
      setQuickSituations,
      isIndeterminate,
      setIndeterminate,

      checkedOrigins,
      setCheckedOrigins,

      handleCheckQuickSituations,
      handleCheckSituations,
      handleCheckAll,
      handleRemoveChip,
      clearAllSituationsFilters,
      isOriginIndeterminate,

      setOrigins,
      handleRemoveOriginChip,
      handleCheckAllOrigins,
      handleCheckOrigins,
      clearAllOriginsFilters,

      cachedCompanies,
      loadingCompanies,

      setSelectedCompany,
      setPeriod,
      setSearchDate,
      setCustomDatePeriod,
      setDateFilter,
      setSituations,
      setRankCriteria,
    }),
    [
      filters,
      filterPeriod,
      columns,
      isGrouped,
      openedDrawer,
      mainLoading,
      refetch,
      mainRefetchLoading,
      cachedMovementData,
      cachedPeriod,
      cachedPeriodTotal,
      cachedSituations,
      isSituationsLoading,
      quickSituations,
      isIndeterminate,
      checkedOrigins,
      handleCheckQuickSituations,
      handleCheckSituations,
      handleCheckAll,
      handleRemoveChip,
      clearAllSituationsFilters,
      isOriginIndeterminate,
      setOrigins,
      handleRemoveOriginChip,
      handleCheckAllOrigins,
      handleCheckOrigins,
      clearAllOriginsFilters,
      cachedCompanies,
      loadingCompanies,
      setSelectedCompany,
      setPeriod,
      setSearchDate,
      setCustomDatePeriod,
      setDateFilter,
      setSituations,
      setRankCriteria,
    ]
  );

  return (
    <RankingClienteContext.Provider value={contextValue}>
      {children}
    </RankingClienteContext.Provider>
  );
};

const useRankingClienteService = () => {
  const context = useContext(RankingClienteContext);

  if (!context) {
    throw new Error(
      "useRankingClienteService must be used with a RankingClienteContextProvider"
    );
  }

  return context;
};

export { RankingClienteProvider, useRankingClienteService };
